<template>
  <b-modal v-model="modal" :title="title" size="md" :no-close-on-backdrop="true" hide-footer centered>
    <!-- Processing -->
    <div v-if="processing" class="text-center py-4">
      <div class="export-icon-wrapper mb-3">
        <i class="bx bx-loader-alt bx-spin"></i>
      </div>
      <h5 class="mb-2">Gerando relatório...</h5>
      <p class="text-muted mb-3 font-size-13">Isso pode levar alguns segundos dependendo da quantidade de dados.</p>
      <div class="progress mx-auto" style="max-width: 350px; height: 8px;">
        <div
          class="progress-bar progress-bar-striped progress-bar-animated bg-success"
          role="progressbar"
          :style="{ width: progressPercent + '%' }"
        ></div>
      </div>
      <p class="text-muted font-size-12 mt-2 mb-0">{{ Math.round(progressPercent) }}%</p>
    </div>

    <!-- Success -->
    <div v-else-if="success" class="text-center py-4">
      <div class="export-icon-wrapper export-icon-success mb-3">
        <i class="bx bx-check-circle"></i>
      </div>
      <h5 class="mb-2">Relatório gerado!</h5>
      <p class="text-muted mb-3 font-size-13">O download foi iniciado automaticamente.</p>
      <b-button variant="success" @click="close()">Fechar</b-button>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-4">
      <div class="export-icon-wrapper export-icon-error mb-3">
        <i class="bx bx-error-circle"></i>
      </div>
      <h5 class="mb-2">Falha ao gerar relatório</h5>
      <p class="text-muted mb-3 font-size-13">{{ errorMessage }}</p>
      <div class="d-flex justify-content-center" style="gap: 10px">
        <b-button variant="light" @click="close()">Fechar</b-button>
        <b-button variant="primary" @click="retry()">
          <i class="bx bx-refresh mr-1"></i> Tentar novamente
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'ExportModal',
  props: {
    title: {
      type: String,
      default: 'Exportar Relatório'
    }
  },
  data () {
    return {
      modal: false,
      processing: false,
      success: false,
      error: false,
      errorMessage: 'Ocorreu um erro inesperado. Tente novamente.',
      progressPercent: 0,
      progressTimer: null,
      exportFn: null
    }
  },
  methods: {
    /**
     * Inicia o export. Recebe uma função que retorna uma Promise com os dados do blob.
     * @param {Function} fn - async function que retorna blob data
     */
    async start (fn) {
      this.exportFn = fn
      this.processing = true
      this.success = false
      this.error = false
      this.errorMessage = 'Ocorreu um erro inesperado. Tente novamente.'
      this.progressPercent = 0
      this.modal = true
      this.startProgress()

      try {
        const res = await fn()
        this.stopProgress()
        this.progressPercent = 100

        await new Promise(resolve => setTimeout(resolve, 400))

        // Trigger download
        this.triggerDownload(res)

        this.processing = false
        this.success = true
      } catch (err) {
        this.stopProgress()
        this.processing = false
        this.error = true

        if (err.response && err.response.data) {
          // Blob error responses need to be parsed
          if (err.response.data instanceof Blob) {
            try {
              const text = await err.response.data.text()
              const json = JSON.parse(text)
              this.errorMessage = json.message || json.error || 'Erro ao gerar relatório.'
            } catch (e) {
              this.errorMessage = 'Erro ao gerar relatório. O servidor pode estar sobrecarregado.'
            }
          } else {
            this.errorMessage = err.response.data.message || err.response.data.error || 'Erro ao gerar relatório.'
          }
        } else {
          this.errorMessage = err.message || 'Erro ao gerar relatório.'
        }
      }
    },

    triggerDownload (data) {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `relatorio-${Date.now()}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },

    startProgress () {
      this.progressTimer = setInterval(() => {
        if (this.progressPercent < 90) {
          const remaining = 90 - this.progressPercent
          const increment = Math.max(0.3, remaining * 0.06)
          this.progressPercent = Math.min(90, this.progressPercent + increment)
        }
      }, 200)
    },

    stopProgress () {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },

    retry () {
      if (this.exportFn) {
        this.start(this.exportFn)
      }
    },

    close () {
      this.modal = false
      this.stopProgress()
    }
  },

  watch: {
    modal (val) {
      if (!val) {
        this.stopProgress()
      }
    }
  },

  beforeDestroy () {
    this.stopProgress()
  }
}
</script>

<style scoped>
.export-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: rgba(85, 110, 230, 0.1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.export-icon-wrapper i {
  font-size: 28px;
  color: #556ee6;
}
.export-icon-success {
  background-color: rgba(52, 195, 143, 0.1) !important;
}
.export-icon-success i {
  color: #34c38f !important;
}
.export-icon-error {
  background-color: rgba(244, 106, 106, 0.1) !important;
}
.export-icon-error i {
  color: #f46a6a !important;
}
</style>
